import { useEffect, useState } from "react";
import { Button, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { CgGoogleTasks } from "react-icons/cg";
import { HiDocumentReport } from "react-icons/hi";
import { MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import collaborationApi from "../../../api/collaborationApi";
import Pagination from "../../../components/Pagination";
import SearchBar from "../SearchBar";
import CopyCollabModal from "./CopyCollabModal";

const PersonalCollabs = () => {
  const [collaborations, setCollaborations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCollab, setSelectedCollab] = useState(null);
  const userEmail = JSON.parse(localStorage.getItem("user"))?.userEmail;
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [sortedColumn, setSortedColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyOption, setCopyOption] = useState("copyNew"); // default selected option
  const [selectedNewCollab, setSelectedNewCollab] = useState(null);
  useEffect(() => {
    if (!userEmail) return;

    setLoading(true);

    const apiCall =
      searchTerm && searchTerm.length > 0
        ? collaborationApi.getSearchCollabs(
            userEmail,
            searchTerm,
            "person",
            page,
            pageSize,
            sortedColumn,
            sortOrder,
          )
        : collaborationApi.getCollabsByPersonUserEmail(
            userEmail,
            page,
            pageSize,
            sortedColumn,
            sortOrder,
          );

    apiCall
      .then((response) => {
        const pageData = response.data;
        const content = pageData?.content || [];

        const mapped = content.map((collab) => ({
          collabId: collab.id,
          createdBy: collab.createdBy,
          collaborationName: collab.collaborationName,
          collaborationDuration: collab.collaborationDuration,
          cost: collab.cost,
          costChargedTo: collab.costChargedTo,
          forPerson: collab.forPerson,
          personName: collab.personName,
          status: collab.status,
          deadline: collab.deadline,
           collabUniqueId: collab.collabUniqueId,
        }));

        setCollaborations(mapped);
        setTotalPages(pageData.totalPages || 0);
        setLoading(false);
      })
      .catch(() => {
        setCollaborations([]);
        setTotalPages(0);
        setLoading(false);
      });
  }, [userEmail, page, pageSize, sortedColumn, sortOrder, searchTerm]);

  const handleBackClick = () => {
    setSelectedCollab(null);
  };
  const handleSearch = (value) => {
    setPage(0); // reset to first page on new search
    setSearchTerm((value ?? searchQuery).trim()); // set searchTerm to trigger search
  };
  const handleTask = (collabId, collaborationName) => {
    navigate(
      `/dashboard/my-collabs/collab-object?collabId=${collabId}&collaborationName=${collaborationName}&tab=brief`,
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }
  const handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value);
    setPageSize(size);
    setPage(0);
  };

  const handlePageClick = (data) => {
    const selectedPage = Math.max(0, Math.min(data.selected, totalPages - 1)); // Ensure selectedPage is within range
    setPage(selectedPage);
    localStorage.setItem("personalCollabs", selectedPage); // Store the page number in localStorage
  };

  const handleSort = (column) => {
    if (sortedColumn === column) {
      // Toggle sort order if the same column is clicked
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Sort by the new column (default to ascending)
      setSortedColumn(column);
      setSortOrder("asc");
    }
  };
  const handleOpenCopyModal = (collab) => {
    setSelectedNewCollab(collab);
    setCopyOption("copyNew"); // default radio selection
    setShowCopyModal(true);
  };

  const handleCloseCopyModal = () => setShowCopyModal(false);
  return (
    <div
      className="scrollable-container"
      style={{
        height: "100%",
        minHeight: "80vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "1rem",
        }}
      >
        <div style={{ width: "250px" }}>
          <SearchBar
            placeholder="Search collaborations..."
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onSearch={handleSearch} // pass handler here
          />
        </div>
      </div>
      {selectedCollab ? (
        <>
          <div className="d-flex justify-content-end align-items-end mt-2">
            <Button variant="secondary" onClick={handleBackClick}>
              Back to Collaboration List
            </Button>
          </div>
        </>
      ) : (
        <>
          <br />
          {collaborations.length === 0 ? (
            <div>No collaborations added yet.</div>
          ) : (
            <div style={{ overflowX: "auto", width: "100%" }}>
            <Table
              striped
              bordered
              hover
              style={{ minWidth: "1100px", whiteSpace: "nowrap" }}
            >
              <thead>
                <tr>
                  <th>CollabId</th>
                  <th
                    onClick={() => handleSort("collaborationName")}
                    style={{ cursor: "pointer" }}
                  >
                    Collaboration Name
                    <span>
                      <span
                        style={{
                          color:
                            sortedColumn === "collaborationName" &&
                            sortOrder === "asc"
                              ? "black"
                              : "gray",
                        }}
                      >
                        ↑
                      </span>{" "}
                      <span
                        style={{
                          color:
                            sortedColumn === "collaborationName" &&
                            sortOrder === "desc"
                              ? "black"
                              : "gray",
                        }}
                      >
                        ↓
                      </span>
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("createdBy")}
                    style={{ cursor: "pointer" }}
                  >
                    Created By
                    <span>
                      <span
                        style={{
                          color:
                            sortedColumn === "createdBy" && sortOrder === "asc"
                              ? "black"
                              : "gray",
                        }}
                      >
                        ↑
                      </span>{" "}
                      <span
                        style={{
                          color:
                            sortedColumn === "createdBy" && sortOrder === "desc"
                              ? "black"
                              : "gray",
                        }}
                      >
                        ↓
                      </span>
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("companyName")}
                    style={{ cursor: "pointer" }}
                  >
                    Company{" "}
                    <span>
                      <span
                        style={{
                          color:
                            sortedColumn === "companyName" &&
                            sortOrder === "asc"
                              ? "black"
                              : "gray",
                        }}
                      >
                        ↑
                      </span>{" "}
                      <span
                        style={{
                          color:
                            sortedColumn === "companyName" &&
                            sortOrder === "desc"
                              ? "black"
                              : "gray",
                        }}
                      >
                        ↓
                      </span>
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("personName")}
                    style={{ cursor: "pointer" }}
                  >
                    Person{" "}
                    <span>
                      <span
                        style={{
                          color:
                            sortedColumn === "personName" && sortOrder === "asc"
                              ? "black"
                              : "gray",
                        }}
                      >
                        ↑
                      </span>{" "}
                      <span
                        style={{
                          color:
                            sortedColumn === "personName" &&
                            sortOrder === "desc"
                              ? "black"
                              : "gray",
                        }}
                      >
                        ↓
                      </span>
                    </span>
                  </th>
                  {/* <th>Duration</th> */}
                  {/* <th>Max Charge</th>
                  <th>Charged To</th> */}
                  <th>createdAt</th>
                  <th>Deadline</th>
                  <th>Collab Price</th>
                  <th>Status</th>
                  <th>Action</th>
                  <th>Audit</th>
                  <th>Copy Collab</th>
                </tr>
              </thead>
              <tbody>
                {collaborations.map((collab) => (
                  <tr key={collab.collabId}>
                    <td>{collab.collabUniqueId || "-"}</td>
                    <td>{collab.collaborationName}</td>
                    <td>
                      {userEmail === collab.createdBy
                        ? "You"
                        : collab.createdBy}
                    </td>
                    <td>{collab.forCompany ? collab.companyName : "-"}</td>
                    <td>{collab.forPerson ? collab.personName : "-"}</td>
                    {/* <td>{collab.collaborationDuration}</td> */}
                    {/* <td>{collab.cost} credits</td>
                    <td>{collab.costChargedTo}</td> */}
                    <td>{collab.createdOn}</td>
                    <td>{collab.deadline}</td>
                    <td>${collab.cost}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {collab.status ? (
                        <span style={{ color: "green" }}>Writable</span>
                      ) : (
                        <span style={{ color: "red" }}>Readable</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "nowrap",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-task">CollabTask</Tooltip>
                        }
                      >
                        <Button
                          variant="info"
                          onClick={() =>
                            handleTask(
                              collab.collabId,
                              collab.collaborationName,
                            )
                          }
                        >
                          <CgGoogleTasks />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-delete">Delete Collab</Tooltip>
                        }
                      >
                        <Button variant="danger" className="ms-2">
                          <MdDelete />
                        </Button>
                      </OverlayTrigger>
                      </div>
                    </td>
                    <td>
                      <Button
                        variant="dark"
                        onClick={() =>
                          navigate(
                            `/dashboard/my-collabs/collab-object?collabId=${
                              collab.collabId
                            }&collaborationName=${encodeURIComponent(
                              collab.collaborationName,
                            )}&tab=audit`,
                          )
                        }
                      >
                        <HiDocumentReport />
                      </Button>
                    </td>
                    <td>
                      <Button
                        variant="secondary"
                        onClick={() => handleOpenCopyModal(collab)}
                      >
                        Copy
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          )}
          {collaborations.length > 0 && totalPages > 0 && (
            <div style={{ marginTop: "auto" }}>
              <Pagination
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                handlePageSizeChange={handlePageSizeChange}
                handlePageClick={handlePageClick}
              />
            </div>
          )}
        </>
      )}
      <CopyCollabModal
        show={showCopyModal}
        onHide={handleCloseCopyModal}
        selectedNewCollab={selectedNewCollab}
        copyOption={copyOption}
        setCopyOption={setCopyOption}
        onConfirm={() => {
          console.log("Copy confirmed for", selectedNewCollab);
          handleCloseCopyModal();
        }}
      />
    </div>
  );
};

export default PersonalCollabs;